import type { DragPayload } from "./payload";
import { tokenReader } from "@/lib/css-tokens";

const FOLDER_ICON =
	'<path d="M1.5 3.5A1 1 0 012.5 2.5h3.172a1 1 0 01.707.293L7.5 3.914a1 1 0 00.707.293H13.5a1 1 0 011 1v7.293a1 1 0 01-1 1h-12a1 1 0 01-1-1V3.5z" fill="currentColor"/>';
const FILE_ICON =
	'<path d="M3 1.5h6.5L13 5v9.5a1 1 0 01-1 1H3a1 1 0 01-1-1v-13a1 1 0 011-1z" fill="none" stroke="currentColor" stroke-width="1.2"/><path d="M9.5 1.5V5H13" stroke="currentColor" stroke-width="1.2"/>';

/** ペイロード種別ごとのアイコンを網羅的に決定する。新しい kind を追加したらここも更新する (コンパイルエラーで検知)。 */
function iconFor(payload: DragPayload): string {
	switch (payload.kind) {
		case "environment-entry":
		case "cloud-shared-volume-entry":
			return payload.entries.some((entry) => entry.entryType === "directory")
				? FOLDER_ICON
				: FILE_ICON;
		case "file-tab":
			return FILE_ICON;
	}
}

/**
 * ドラッグ中にカーソルへ追従する独自のドラッグ画像 (アイコン + 名前のピル) を生成する。
 * setDragImage はドラッグ開始時点の DOM スナップショットを撮るため、要素を一旦 body に
 * 追加して可視状態にし、スナップショット取得後の次フレームで取り除く。返り値の offsetX /
 * offsetY はカーソルとピルの相対位置。
 */
export function createDragImage(payload: DragPayload): {
	element: HTMLElement;
	offsetX: number;
	offsetY: number;
} {
	const pill = document.createElement("div");
	const token = tokenReader();
	// 画面外に配置して既存レイアウトへ影響させずにレンダリングさせる。
	pill.style.cssText = [
		"position:fixed",
		"top:-1000px",
		"left:-1000px",
		"display:flex",
		"align-items:center",
		"gap:6px",
		"padding:4px 8px",
		`background:${token("--surface-2")}`,
		`border:1px solid ${token("--accent-line")}`,
		"border-radius:4px",
		`box-shadow:${token("--shadow-popover")}`,
		`font-size:${token("--text-caption")}`,
		`color:${token("--fg-1")}`,
		"white-space:nowrap",
		"pointer-events:none",
	].join(";");

	pill.innerHTML = `<svg width="14" height="14" viewBox="0 0 16 16" fill="none">${iconFor(payload)}</svg>`;

	const label = document.createElement("span");
	label.textContent =
		payload.kind !== "file-tab"
			? payload.entries.length > 1
				? `${payload.entries[0].name} ほか${payload.entries.length - 1}件`
				: payload.entries[0].name
			: payload.name;
	pill.appendChild(label);

	document.body.appendChild(pill);
	requestAnimationFrame(() => pill.remove());

	// 負の offsetX でピルをカーソルの右側にずらす (Chromium 系で有効)。
	return { element: pill, offsetX: -14, offsetY: -12 };
}
